import * as React from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

export const PrivacyContent: React.FC = () => (
  <div className='flex flex-col gap-6 text-left'>
    <div className='mb-4'>
      <h1 className='mb-2 font-sans text-24 font-bold leading-[32px] text-brand-800 md:text-32 md:leading-[40px]'>
        DECHOES Privacy Policy
      </h1>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Last Updated: 25.01.2026
      </p>
    </div>

    <section>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        This Privacy Policy (the “Policy”) describes how DECHOAI S.R.L. (the “Company”, “DECHOES”,
        “we”, “us”, “our”) collects and processes Personal Data in connection with the DECHOES
        mobile application and website www.dechoes.com (together, the “Services”).
        <br />
        <br />
        Company details (data controller):
        <br />
        DECHOAI S.R.L., Voluntari, 1-8M Pipera boulevard, Block T4, Ground Floor, apartment B10,
        Ilfov county, Romania, Trade Register no. J2025063859004, unique registration number
        52387032.
        <br />
        Support: support@dechoai.com
        <br />
        <br />
        Capitalized terms not defined in this Policy have the meaning set out in the DECHOES Terms
        and Conditions.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Introduction
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We respect your privacy and are committed to protecting the Personal Data we process about
        you. This Policy explains our practices with respect to Personal Data processed about Users
        of the Services, including Users who create, train, configure, or publish Echoes
        (“Creators”) and Users who interact with Echoes (“Customers”).
        <br />
        <br />
        We process Personal Data when you use the Services and for the provision and security of the
        Services. Unless stated otherwise, we act as a data controller, meaning we determine why and
        how Personal Data is processed.
        <br />
        <br />
        If you have questions about this Policy or our processing of your Personal Data, contact us
        using the details in Contact Us.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        What is Personal Data?
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        “Personal Data” means any information that identifies or relates to an identified or
        identifiable natural person, including information that can reasonably be linked to you
        directly or indirectly.
        <br />
        We may also process aggregated, anonymized, or de-identified information that is not
        reasonably linkable to you; such information is not Personal Data and is not subject to this
        Policy.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Informing Us of Changes
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        It is important that the Personal Data we hold about you is accurate and current. You may
        update certain account information through your account settings or by contacting us.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Eligibility
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        The Services are intended for individuals 18 years of age or older (or the age of majority
        in your jurisdiction). If we become aware that an individual does not meet the age
        requirement, we may restrict or terminate access.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Third-Party Links
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        The Services may contain links to third-party websites, plug-ins, or applications. We are
        not responsible for third-party privacy practices. Please review the privacy policies of
        those third parties.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        If You Do Not Wish to Provide Personal Data
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We require certain Personal Data to provide the Services and comply with legal obligations.
        If you do not provide required data, certain features (including creating, publishing, or
        interacting with Echoes) may be unavailable.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Updates to this Policy
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We may update this Policy from time to time. Updates are effective when posted in the
        Services. Where required by law, we will provide notice of material changes via the Services
        and/or email.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Information We Collect
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        The types of Personal Data we process depend on how you use the Services. We collect
        information in three ways: (a) information you provide, (b) information collected
        automatically, and (c) information from third parties.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Information you provide directly to us
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>We may collect:</p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Identifiers: name, username/display name, email address, phone number, account
          identifiers.
        </li>
        <li>Profile and account data: settings, preferences, avatars, bios, account status.</li>
        <li>
          Creator and Echo-related content: prompts, configurations, training inputs, uploaded media
          (photos, videos, audio/voice samples), and other content used to create or operate Echoes.
        </li>
        <li>Communications: support messages, feedback, reports, and other correspondence.</li>
        <li>
          Payment information (if applicable): billing details, transaction records, and payment
          confirmation data. Payment card processing is typically handled by payment providers; we
          may receive tokens and limited card metadata (e.g., last four digits), depending on
          provider setup.
        </li>
        <li>
          Legal / compliance data (where required): information necessary for fraud prevention,
          dispute handling, or legal compliance.
        </li>
      </ul>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Information we collect automatically
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We and our service providers may collect:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Device and network data: IP address, device identifiers, browser/app version, operating
          system, user-agent, language, and approximate location derived from IP.
        </li>
        <li>
          Usage and interaction data: pages/screens viewed, session logs, clicks, feature usage,
          interaction duration, error logs, and performance diagnostics.
        </li>
        <li>
          Cookies and similar technologies (website): cookies, SDKs, and similar tools, where
          applicable and subject to your choices and legal requirements. For details, see our Cookie
          Notice (if/when published).
        </li>
      </ul>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Information we collect from other sources
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>We may receive:</p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Third-party account login data (if you choose SSO): basic account identifiers such as
          email/username and authentication tokens from the provider, subject to your permissions.
        </li>
        <li>
          Service provider data: verification results, fraud signals, and security-related
          information from vendors.
        </li>
        <li>
          Publicly available information: limited information used for safety, rights protection, or
          platform integrity.
        </li>
      </ul>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Echo-Specific Data Processing
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        DECHOES enables creation and interaction with Echoes (AI-generated digital simulations).
        When you create or interact with an Echo, we may process:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Training inputs and configuration data (text prompts, persona settings, interaction
          rules);
        </li>
        <li>Uploaded media (e.g., voice samples, images, videos) if you choose to provide them;</li>
        <li>Conversation logs and interaction outputs (text/audio) generated during sessions;</li>
        <li>Safety signals and moderation metadata.</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        You should not upload Personal Data of others (including voice, images, or identifying info)
        unless you have a valid legal basis (e.g., verifiable consent) and your use complies with
        the Terms.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Age / Identity Verification
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We may use third-party verification providers to verify age and/or identity where required
        by law, platform policy, risk signals, or for certain features.
        <br />
        Depending on the verification method and your jurisdiction:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          The provider may collect government ID images and selfies and produce a verification
          result (pass/fail and reason).
        </li>
        <li>
          Where face-based verification is used, any biometric/face recognition data (if processed)
          is typically processed by the provider acting as our processor, and we may not receive the
          underlying biometric templates—only verification outcomes and associated metadata—unless
          permitted and required by law and disclosed at the time of collection.
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        If you need to withdraw consent for any consent-based verification retention (where
        applicable), you may contact us at support@dechoai.com (or a dedicated privacy email if you
        create one).
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Regional Privacy Disclosures
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        These Regional Privacy Disclosures supplement the DECHOES Privacy Policy and apply depending
        on your place of residence. If there is a conflict between these disclosures and the main
        Policy, these disclosures prevail for residents of the applicable region.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Data Controller
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        For purposes of the EU General Data Protection Regulation (“GDPR”) and the UK GDPR, the data
        controller is:
        <br />
        DECHOAI S.R.L.
        <br />
        Voluntari, 1-8M Pipera boulevard, Block T4, Ground Floor, apartment B10
        <br />
        Ilfov county, Romania
        <br />
        Email: support@dechoai.com
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Lawful Bases for Processing
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We process Personal Data under one or more of the following lawful bases:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Performance of a contract – to provide the Services, operate accounts, enable Echo
          creation and interaction, process payments, and provide support
        </li>
        <li>
          Legitimate interests – to secure the Services, prevent fraud and misuse, improve AI models
          and platform functionality, enforce our Terms, and protect users and third parties
        </li>
        <li>
          Consent – where required (e.g., certain cookies, optional verification methods, or
          features explicitly requesting consent)
        </li>
        <li>
          Legal obligation – to comply with applicable laws, court orders, and regulatory
          requirements
        </li>
        <li>Public interest – where required to report or prevent illegal activity</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        We only process Personal Data where a lawful basis applies.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        International Transfers
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Where Personal Data is transferred outside the EEA or the UK, we rely on appropriate
        safeguards, including adequacy decisions or standard contractual clauses, in accordance with
        applicable law.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        United States State Privacy Disclosures
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        This section applies to residents of U.S. states with comprehensive privacy laws, including
        California (CPRA), Colorado (CPA), Virginia (VCDPA), Connecticut (CTDPA), Utah (UCPA), and
        other similar laws.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Categories of Personal Data Collected
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Depending on your use of the Services, we may collect:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Identifiers (e.g., name, email address, username, IP address)</li>
        <li>Account and profile information</li>
        <li>Commercial information (e.g., subscriptions, transactions, credits)</li>
        <li>Internet or network activity (e.g., device data, usage logs)</li>
        <li>User-generated content (e.g., Echo prompts, interactions, media uploads)</li>
        <li>Inferences (e.g., preferences derived from usage patterns)</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        We do not knowingly collect Personal Data from minors.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Sensitive Personal Information
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Where applicable, we may process limited sensitive personal information (as defined under
        U.S. state laws), such as login credentials or verification-related data, solely for
        purposes permitted by law, including security, fraud prevention, identity verification, and
        service provision.
        <br />
        We do not use sensitive personal information to infer characteristics or for targeted
        advertising.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Sale or Sharing of Personal Data
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>DECHOES:</p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Does not sell Personal Data</li>
        <li>
          Does not share Personal Data for cross-context behavioral advertising as defined under
          California law
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        If this changes in the future, we will update this Policy and provide legally required
        opt-out mechanisms.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Your U.S. Privacy Rights
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Depending on your state of residence, you may have the right to:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Know what Personal Data we collect, use, and disclose</li>
        <li>Access and obtain a copy of your Personal Data</li>
        <li>Correct inaccurate Personal Data</li>
        <li>Request deletion of Personal Data</li>
        <li>Opt out of targeted advertising (if applicable)</li>
        <li>Opt out of certain profiling activities</li>
        <li>Not receive discriminatory treatment for exercising privacy rights</li>
      </ul>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Exercising Your Rights
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        You may submit a privacy rights request by contacting:
        <br />
        <EnvelopeIcon className='inline size-4 align-text-bottom' /> support@dechoai.com
        <br />
        We may take reasonable steps to verify your identity before fulfilling your request.
        <br />
        Authorized agents may submit requests on your behalf where permitted by law and upon
        verification of authorization.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Contact for Privacy Matters
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        For questions, requests, or complaints related to privacy:
        <br />
        DECHOAI S.R.L.
        <br />
        Email: support@dechoai.com
        <br />
        Address: Voluntari, 1-8M Pipera boulevard, Block T4, Ground Floor, apartment B10, Ilfov
        county, Romania
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Use of AI Systems and Profiling
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        DECHOES uses artificial intelligence systems, including machine-learning models, to operate
        core features of the Services, such as enabling interactions with Echoes, generating
        AI-based content, improving system performance, detecting misuse, and maintaining platform
        safety.
        <br />
        <br />
        In this context, we may process Personal Data through automated means, including profiling,
        as defined under applicable data protection law. Profiling refers to the automated
        processing of Personal Data to evaluate certain aspects relating to a User, such as
        preferences, interests, usage patterns, or behavioral signals within the Services.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Nature of Automated Processing
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Automated processing within DECHOES may include, without limitation:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Generating Echo responses and interactions based on user inputs and configuration settings
        </li>
        <li>
          Personalizing content, recommendations, or feature availability based on usage patterns
        </li>
        <li>Detecting potentially harmful, abusive, fraudulent, or policy-violating activity</li>
        <li>
          Applying safety filters, moderation signals, or risk scores to protect users and the
          platform
        </li>
        <li>
          Improving and testing AI models and system functionality using aggregated or de-identified
          data
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        Such processing is carried out using probabilistic AI models and algorithmic systems and
        does not involve human cognition or intent.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        No Solely Automated Decisions with Legal or Similar Effects
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        DECHOES does not engage in solely automated decision-making, including profiling, that
        produces legal effects concerning you or similarly significant effects within the meaning of
        Article 22 of the GDPR.
        <br />
        <br />
        In particular:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          AI systems do not make binding decisions that affect your legal rights, contractual
          status, or legal obligations without meaningful human involvement where required by law
        </li>
        <li>
          Echo outputs are informational, simulated, and non-binding and do not constitute decisions
          about you
        </li>
        <li>
          Where automated systems are used to support account safety, enforcement, or compliance
          decisions, such systems are designed to operate alongside procedural safeguards and, where
          appropriate, human review.
        </li>
      </ul>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        EU AI Act Transparency
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        In accordance with the EU Artificial Intelligence Act, you are expressly informed that:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>You are interacting with AI-generated systems and outputs, not humans</li>
        <li>Echo content is artificially generated or manipulated</li>
        <li>Any apparent likeness, personality, or voice is synthetic and simulated</li>
        <li>Outputs are generated automatically and may be inaccurate or fictional</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        Where required by law, DECHOES applies AI-generated labels, disclosures, watermarks, or
        metadata to AI outputs. Users must not remove or obscure such disclosures.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Lawful Bases for Profiling
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Where GDPR applies, profiling and automated processing are carried out on one or more of the
        following lawful bases:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Performance of a contract – to provide and operate the Services and Echo functionality
        </li>
        <li>
          Legitimate interests – to improve system performance, ensure security, prevent misuse, and
          enhance user experience
        </li>
        <li>Consent – where required by law for specific features or processing activities</li>
      </ul>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        Your Rights in Relation to Profiling
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Subject to applicable law, you have the right to:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Obtain meaningful information about the logic involved in profiling</li>
        <li>
          Object to profiling carried out on the basis of legitimate interests, where applicable
        </li>
        <li>
          Request human intervention, express your point of view, or contest certain decisions,
          where legally required
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        You may exercise these rights by contacting us at support@dechoai.com.
      </p>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>Safeguards</h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We implement appropriate technical and organizational measures to ensure that automated
        processing and profiling are:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Transparent and proportionate</li>
        <li>Used only for legitimate and defined purposes</li>
        <li>Subject to security, access controls, and monitoring</li>
        <li>Regularly reviewed to mitigate bias, errors, or unintended outcomes</li>
      </ul>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        How We Use Personal Data
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We process Personal Data for purposes including:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Providing, operating, and maintaining the Services;</li>
        <li>Creating and operating Echoes and enabling user interactions;</li>
        <li>Personalizing content and improving user experience;</li>
        <li>Processing payments and managing subscriptions/credits;</li>
        <li>Customer support and communications;</li>
        <li>
          Safety, trust, and content moderation (including preventing harmful or illegal content);
        </li>
        <li>Fraud prevention, security monitoring, and enforcing our Terms;</li>
        <li>Analytics and performance measurement;</li>
        <li>
          Model improvement and service development (including improving AI features), subject to
          applicable law and your settings/choices where required;
        </li>
        <li>Complying with legal obligations and responding to lawful requests.</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        We may combine information we collect through the Services with information from other
        sources, and we may aggregate or de-identify information for analytics, research, and
        service improvement.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        When We Disclose Personal Data
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We may disclose Personal Data to:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>
          Service providers and vendors: hosting, analytics, payments, customer support, moderation,
          fraud prevention, verification providers, and security vendors (as processors/contracted
          partners);
        </li>
        <li>Affiliates (if any): for internal administration consistent with this Policy;</li>
        <li>Professional advisers: lawyers, auditors, insurers, consultants as needed;</li>
        <li>
          Authorities and regulators: where required by law or to protect rights, safety, and
          integrity of the platform;
        </li>
        <li>
          Business transfers: in connection with a merger, acquisition, financing, or sale of
          assets;
        </li>
        <li>
          Other parties at your direction: when you choose to share content publicly or otherwise
          direct disclosure.
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        Public/Shared Content: If you publish a Public Echo or share content with others, certain
        information may become visible to other users or the public, depending on your settings and
        actions.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        International Transfers
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We may transfer Personal Data to countries outside your jurisdiction (including outside the
        EEA/UK/Switzerland), for example where our vendors or infrastructure are located. Where
        required, we use appropriate safeguards (e.g., adequacy decisions or contractual safeguards)
        in accordance with applicable law.
        <br />
        <br />
        We do not transfer your data outside the European Economic Area (EEA) unless adequate
        safeguards are in place (e.g. Standard Contractual Clauses).
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Your Choices and Rights
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        You may have rights depending on where you live, including the right to:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Request access to, correction of, or deletion of Personal Data;</li>
        <li>Object to or restrict certain processing;</li>
        <li>Withdraw consent where processing is based on consent;</li>
        <li>Request portability of certain data;</li>
        <li>Lodge a complaint with a competent data protection authority.</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        You can manage certain settings in the Services. To exercise rights, contact us using the
        details in Contact Us. We may take reasonable steps to verify your identity before
        fulfilling requests.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Cookies and Similar Technologies
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Where applicable (especially on the website), we may use cookies/SDKs for authentication,
        security, analytics, and feature functionality. Where required by law, we will obtain
        consent for non-essential cookies. See our Cookie Notice (if/when published) for details.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Data Retention
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We retain Personal Data only as long as reasonably necessary for the purposes described in
        this Policy, including:
      </p>
      <ul className='list-disc pl-5 font-sans text-16 font-normal leading-24 text-gray-600 marker:text-brand-800'>
        <li>Account lifecycle and service provision;</li>
        <li>Safety and security investigations and enforcement;</li>
        <li>Legal and regulatory compliance (including retention required by law);</li>
        <li>Dispute resolution and legal claims.</li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        Retention periods may vary depending on data type, feature use, and legal requirements.
        Where feasible, we delete, anonymize, or de-identify data after retention periods expire.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Security
      </h4>

      <h5 className='mb-2 mt-4 font-sans text-16 font-bold leading-24 text-gray-600'>
        A. Data Security Measures
      </h5>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        We implement appropriate technical and organizational measures to ensure a level of security
        appropriate to the risks related to the processing of your personal data through the
        Website, including: SSL/TLS encryption for all data in transit, encryption of data at rest
        for stored files and database records, secure authentication through JWT tokens with
        automatic expiration and refresh mechanisms, industry-standard password hashing
        (PBKDF2-SHA256), OAuth 2.0 secure third-party authentication, user-scoped data isolation
        ensuring each user can only access their own data, PCI DSS-compliant payment processing
        through Stripe (we never store or have access to payment card details), input validation and
        sanitization on all API endpoints, containerized infrastructure with service-level
        isolation, automated error monitoring and incident detection, and bot protection through
        reCAPTCHA verification.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Children&apos;s Privacy
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        The Services are not intended for minors. We do not knowingly process Personal Data from
        individuals under the applicable minimum age. If you believe a minor has provided us
        Personal Data, contact us.
      </p>
    </section>

    <section>
      <h4 className='mb-[10px] font-sans text-18 font-medium leading-25 text-dark-100 md:text-20 md:leading-27'>
        Contact Us
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        If you have questions about this Policy or wish to exercise your privacy rights, contact us
        at:
        <br />
        support@dechoai.com
        <br />
        Controller address:
        <br />
        DECHOAI S.R.L., Voluntari, 1-8M Pipera boulevard, Block T4, Ground Floor, apartment B10,
        Ilfov county, Romania.
      </p>
    </section>
  </div>
);
